
import { Autocomplete, Avatar, Box, FormControl, InputAdornment, TextField, useTheme } from '@mui/material'
import Popper from '@mui/material/Popper'
import React, { useEffect, useState } from 'react'
import { renderTeamOption } from '../Team/renderOptions'

interface MassSingleUserSelectProps {
  name: string
  value: any // Expect a user object or null
  options: any[]
  displayLabel: string
  onChange: (e: React.ChangeEvent<{ name?: string; value: any }>) => void
  inputRef?: React.RefObject<HTMLInputElement>
  disabled?: boolean
  sx?: object
  setNotifyIds?: React.Dispatch<React.SetStateAction<string[]>>
}

const CustomPopper = (props: any) => {
  return (
    <Popper
      {...props}
      placement="bottom-start"
      modifiers={[
        {
          name: 'offset',
          options: {
            offset: [0, 4],
          },
        },
      ]}
      sx={{
        zIndex: 1300,
        minWidth: 250,
        maxWidth: 420,
        width: 'auto',
      }}
    />
  )
}

const MassSingleUserSelect: React.FC<MassSingleUserSelectProps> = ({
  name,
  value,
  options = [],
  displayLabel,
  onChange,
  inputRef,
  disabled,
  sx,
  setNotifyIds,
}) => {
  const theme = useTheme()
  // Handle value as either a user_uuid string or a user object
  const [selectedUser, setSelectedUser] = useState<any>(() => {
    if (!value) return null
    // If value is a string (user_uuid), find the user object
    if (typeof value === 'string') {
      return options.find(opt => opt.user_uuid === value) || null
    }
    // If value is already an object, use it
    return value
  })

  useEffect(() => {
    if (!value) {
      setSelectedUser(null)
      return
    }
    // If value is a string (user_uuid), find the user object
    if (typeof value === 'string') {
      const user = options.find(opt => opt.user_uuid === value)
      setSelectedUser(user || null)
    } else {
      setSelectedUser(value)
    }
  }, [value, options])
  const handleChange = (_event: any, newValue: any | null) => {
    if (typeof onChange === 'function') {
      // Send the full user object to support queue field format: { role, user_uuid, ... }
      // This is required for proper queue updates and notifications
      onChange({
        target: {
          name,
          value: newValue ?? null,
        } as EventTarget & { name?: string; value: any },
      } as React.ChangeEvent<{ name?: string; value: any }>)
    }
    // Keep the full object locally for display purposes
    setSelectedUser(newValue)
  }

  return (
    <Box sx={{ width: '100%', ...sx }}>
      <FormControl fullWidth>
        <Autocomplete
          options={options || []}
          PopperComponent={CustomPopper}
          value={options.find(opt => opt.user_uuid === selectedUser?.user_uuid) || null}
          onChange={handleChange}
          getOptionLabel={option => option?.name || ''}
          isOptionEqualToValue={(option, value) => {
            if (!option || !value) return false
            return option.user_uuid === value.user_uuid
          }}
          filterOptions={(options, { inputValue }) => {
            const searchTerm = inputValue.toLowerCase()
            return options.filter(
              option =>
                option.name?.toLowerCase().includes(searchTerm) ||
                (option.role && option.role.toLowerCase().includes(searchTerm))
            )
          }}
          renderOption={(props, option, state) => renderTeamOption(props, option, state, theme)}
          renderInput={params => (
            <TextField
              {...params}
              label={displayLabel}
              variant="outlined"
              inputRef={inputRef}
              placeholder="Search by name or role"
              InputProps={{
                ...params.InputProps,
                startAdornment: selectedUser?.image_512 ? (
                  <InputAdornment position="start">
                    <Avatar
                      src={selectedUser.image_512}
                      alt={selectedUser.name}
                      sx={{ width: 30, height: 30, mr: 1 }}
                    />
                  </InputAdornment>
                ) : null,
                endAdornment: null,
              }}
              sx={{
                maxHeight: '45px',
                '& .MuiAutocomplete-inputRoot': {
                  maxHeight: '45px',
                  height: '45px',
                },
                '& .MuiAutocomplete-input': {
                  minWidth: 140, // Adjust this to allow more name space
                  flex: '1 1 auto',
                },
                '& .MuiInputBase-inputAdornedStart': {
                  paddingLeft: 0,
                },
              }}
            />
          )}
          disabled={disabled}
          fullWidth
          sx={{
            '& .MuiInputBase-root': {
              margin: 0,
              p: 0,
              paddingLeft: 1,
            },
          }}
        />
      </FormControl>
    </Box>
  )
}

export default MassSingleUserSelect
